import CAHA1220RequestModel from '@/api/data/CAH/CAHA1220/CAHA1220RequestModel'
import CAHA1220ResponseModel from '@/api/data/CAH/CAHA1220/CAHA1220ResponseModel'

import RestService from '@/common/RestService'
import ApiUrlConstants from '@/common/ApiUrlConstants'
import ValidationUtil from '@/common/utils/ValidationUtil'
import CommonUtil from '@/common/utils/CommonUtil'
import Store from '@/common/utils/Store'

export default class CAHG1220Service {
  /**
   * 年金受取額シミュレーション計算結果取得リクエストモデル
   */
  private caha1220RequestModel = new CAHA1220RequestModel()
  /**
   * CAHA1220 API処理呼出
   * @param reservedAmount
   * @param receiptStartAge
   * @param pensionType
   * @param receiptPeriodAc
   * @param gender
   * @param warrantyPeriod
   * @param receiptPeriodLa
   * @param receiptPeriodIa
   * @param investmentYield
   * @returns response model
   */

  /**
   * 年金受取額シミュレーション計算結果取得呼び出し（非同期処理）
   */
  public async callAPICAHA1220(
    reservedAmount: string,
    receiptStartAge: string,
    pensionType: string,
    receiptPeriodAc: string,
    gender: string,
    warrantyPeriod: string,
    receiptPeriodLa: string,
    receiptPeriodIa: string,
    investmentYield: string
  ): Promise<CAHA1220ResponseModel> {
    this.caha1220RequestModel.reservedAmount = reservedAmount
    this.caha1220RequestModel.receiptStartAge = receiptStartAge
    this.caha1220RequestModel.pensionType = pensionType
    this.caha1220RequestModel.receiptPeriodAc = receiptPeriodAc
    this.caha1220RequestModel.gender = gender
    this.caha1220RequestModel.warrantyPeriod = warrantyPeriod
    this.caha1220RequestModel.receiptPeriodLa = receiptPeriodLa
    this.caha1220RequestModel.receiptPeriodIa = receiptPeriodIa
    this.caha1220RequestModel.investmentYield = investmentYield

    const caha1220ResponseModel: CAHA1220ResponseModel = <
      CAHA1220ResponseModel
    > await RestService.post(
      CommonUtil.getApiUrl(ApiUrlConstants.CAHA1220),
      this.caha1220RequestModel
    )
    return caha1220ResponseModel
  }

  /**
   * 入力項目バリデーション
   * @param reservedAmount
   * @param receiptStartAge
   * @param pensionType
   * @param receiptPeriodAc
   * @param gender
   * @param warrantyPeriod
   * @param receiptPeriodLa
   * @param receiptPeriodIa
   * @param investmentYield
   * @returns isValid boolean
   */

  /* 入力項目チェック処理 */
  public validationInput(
    reservedAmount: string,
    receiptStartAge: string,
    pensionType: string,
    receiptPeriodAc: string,
    gender: string,
    warrantyPeriod: string,
    receiptPeriodLa: string,
    receiptPeriodIa: string,
    investmentYield: string
  ) {
    /* 相関チェックフラグ */
    let corCheckFlg = false
    /* エラー格納配列 */
    const errorValid = []

    /* 年金受取開始時点の積立金額必須チェック */
    if (ValidationUtil.isNotEmptyValid(reservedAmount) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF001',
        message: CommonUtil.getMessage('MSGCAHG1220WF001', [])
      })
      /* 年金受取開始時点の積立金額半角数字チェック */
    } else if (ValidationUtil.isHankakuNumValid(reservedAmount) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF002',
        message: CommonUtil.getMessage('MSGCAHG1220WF002', [])
      })
      /* 年金受取開始時点の積立金額入力範囲チェック */
    } else if (ValidationUtil.isExplValid(1, 5000, reservedAmount) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF003',
        message: CommonUtil.getMessage('MSGCAHG1220WF003', [])
      })
    }

    /* 年金の受取開始年齢必須チェック */
    if (ValidationUtil.isNotEmptyValid(receiptStartAge) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF004',
        message: CommonUtil.getMessage('MSGCAHG1220WF004', [])
      })

      corCheckFlg = true

      /* 年金の受取開始年齢半角数字チェック */
    } else if (ValidationUtil.isHankakuNumValid(receiptStartAge) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF005',
        message: CommonUtil.getMessage('MSGCAHG1220WF005', [])
      })

      corCheckFlg = true

      /* 年金の受取開始年齢入力範囲チェック */
    } else if (ValidationUtil.isExplValid(60, 75, receiptStartAge) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF006',
        message: CommonUtil.getMessage('MSGCAHG1220WF006', [])
      })

      corCheckFlg = true
    }

    /* 年金の種類必須チェック */
    if (ValidationUtil.isNotEmptyValid(pensionType) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF007',
        message: CommonUtil.getMessage('MSGCAHG1220WF007', [])
      })
      /* 年金の種類半角数字チェック */
    } else if (ValidationUtil.isHankakuNumValid(pensionType) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF008',
        message: CommonUtil.getMessage('MSGCAHG1220WF008', [])
      })
      /* 年金の種類入力範囲チェック */
    } else if (ValidationUtil.isExplValid(1, 3, pensionType) === false) {
      errorValid.push({
        code: 'MSGCAHG1220WF009',
        message: CommonUtil.getMessage('MSGCAHG1220WF009', [])
      })
    }

    /* 年金の種類が確定年金の場合 */
    if (pensionType === '1') {
      /* 年金の受取期間（確定年金）必須チェック */
      if (ValidationUtil.isNotEmptyValid(receiptPeriodAc) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF010',
          message: CommonUtil.getMessage('MSGCAHG1220WF010', [])
        })
        /* 年金の受取期間（確定年金）半角数字チェック */
      } else if (ValidationUtil.isHankakuNumValid(receiptPeriodAc) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF011',
          message: CommonUtil.getMessage('MSGCAHG1220WF011', [])
        })
        /* 年金の受取期間（確定年金）入力範囲チェック */
      } else if (ValidationUtil.isExplValid(0, 3, receiptPeriodAc) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF012',
          message: CommonUtil.getMessage('MSGCAHG1220WF012', [])
        })
      }
    }

    /* 年金の種類が終身年金の場合 */
    if (pensionType === '2') {
      /* 性別必須チェック */
      if (ValidationUtil.isNotEmptyValid(gender) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF013',
          message: CommonUtil.getMessage('MSGCAHG1220WF013', [])
        })
        /* 性別半角数字チェック */
      } else if (ValidationUtil.isHankakuNumValid(gender) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF014',
          message: CommonUtil.getMessage('MSGCAHG1220WF014', [])
        })
        /* 性別入力範囲チェック */
      } else if (ValidationUtil.isExplValid(0, 1, gender) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF015',
          message: CommonUtil.getMessage('MSGCAHG1220WF015', [])
        })
      }
      /* 終身年金の保証期間必須チェック */
      if (ValidationUtil.isNotEmptyValid(warrantyPeriod) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF016',
          message: CommonUtil.getMessage('MSGCAHG1220WF016', [])
        })

        corCheckFlg = true

        /* 終身年金の保証期間半角数字チェック */
      } else if (ValidationUtil.isHankakuNumValid(warrantyPeriod) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF017',
          message: CommonUtil.getMessage('MSGCAHG1220WF017', [])
        })

        corCheckFlg = true

        /* 終身年金の保証期間入力範囲チェック */
      } else if (ValidationUtil.isExplValid(0, 3, warrantyPeriod) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF018',
          message: CommonUtil.getMessage('MSGCAHG1220WF018', [])
        })

        corCheckFlg = true
      }
      /* 生存期間（何歳まで受け取りますか？）必須チェック */
      if (ValidationUtil.isNotEmptyValid(receiptPeriodLa) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF019',
          message: CommonUtil.getMessage('MSGCAHG1220WF019', [])
        })

        corCheckFlg = true

        /* 生存期間（何歳まで受け取りますか？）半角数字チェック */
      } else if (ValidationUtil.isHankakuNumValid(receiptPeriodLa) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF020',
          message: CommonUtil.getMessage('MSGCAHG1220WF020', [])
        })

        corCheckFlg = true

        /* 生存期間（何歳まで受け取りますか？）入力範囲チェック */
      } else if (
        ValidationUtil.isExplValid(65, 120, receiptPeriodLa) === false
      ) {
        errorValid.push({
          code: 'MSGCAHG1220WF021',
          message: CommonUtil.getMessage('MSGCAHG1220WF021', [])
        })

        corCheckFlg = true
      }

      if (corCheckFlg !== true) {
        /* 年金の受取開始年齢+終身年金の保証期間と生存期間（何歳まで受け取りますか？）の相関チェック（入力範囲チェックを利用） */
        if (
          ValidationUtil.isExplValid(
            Number(receiptStartAge) + 5 * (Number(warrantyPeriod) + 1),
            120,
            receiptPeriodLa
          ) === false
        ) {
          errorValid.push({
            code: 'MSGCAHG1220WF022',
            message: CommonUtil.getMessage('MSGCAHG1220WF022', [
              Number(receiptStartAge) + 5 * (Number(warrantyPeriod) + 1)
            ])
          })
        }
      }
    }

    /* 年金の種類が分割取崩年金の場合 */
    if (pensionType === '3') {
      /* 年金の受取期間（分割取崩年金）必須チェック */
      if (ValidationUtil.isNotEmptyValid(receiptPeriodIa) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF023',
          message: CommonUtil.getMessage('MSGCAHG1220WF023', [])
        })
        /* 年金の受取期間（分割取崩年金）半角数字チェック */
      } else if (ValidationUtil.isHankakuNumValid(receiptPeriodIa) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF024',
          message: CommonUtil.getMessage('MSGCAHG1220WF024', [])
        })
        /* 年金の受取期間（分割取崩年金）入力範囲チェック */
      } else if (ValidationUtil.isExplValid(0, 3, receiptPeriodIa) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF025',
          message: CommonUtil.getMessage('MSGCAHG1220WF025', [])
        })
      }

      /* 年金受取期間中の運用利回り必須チェック */
      if (ValidationUtil.isNotEmptyValid(investmentYield) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF026',
          message: CommonUtil.getMessage('MSGCAHG1220WF026', [])
        })
        /* 年金受取期間中の運用利回り数値チェック */
      } else if (ValidationUtil.isNumberValid(investmentYield) === false) {
        errorValid.push({
          code: 'MSGCAHG1220WF027',
          message: CommonUtil.getMessage('MSGCAHG1220WF027', [])
        })
        /* 年金受取期間中の運用利回り入力範囲チェック */
      } else if (
        ValidationUtil.isExplValid(0.0, 20.0, investmentYield) === false
      ) {
        errorValid.push({
          code: 'MSGCAHG1220WF028',
          message: CommonUtil.getMessage('MSGCAHG1220WF028', [])
        })
      }
    }
    return errorValid
  }
}
